import * as z from 'zod';

export const ListSupportedLocalesRequest = z.object({
  options: z
    .object({
      languageCode: z
        .string()
        .describe(
          'Language to filter by. For example, `"en"` for all English locales.'
        )
        .optional()
        .nullable(),
      includeAllLocales: z
        .boolean()
        .describe(
          'Whether to include all possible regional variants for each language code in the response. When `false`, only the default locale, such as `"en-US"`, is returned and regional variants like `"en-GB"` and `"en-AU"` are excluded.'
        )
        .optional()
        .nullable(),
      includeRegionOptions: z
        .boolean()
        .describe(
          'Whether to include region-specific options such as flags and regional codes for each locale in the response.'
        )
        .optional()
        .nullable(),
    })
    .optional(),
});
export const ListSupportedLocalesResponse = z.object({
  supportedLocales: z
    .array(
      z.object({
        _id: z.string().optional().nullable(),
        languageCode: z
          .string()
          .describe('Language code.')
          .optional()
          .nullable(),
        regionCode: z.string().describe('Region code.').optional().nullable(),
        defaultFlag: z
          .string()
          .describe('Default flag code for the locale.')
          .max(3)
          .optional()
          .nullable(),
        defaultRegionalFormat: z
          .string()
          .describe(
            'Default format for how to display data types such as dates, times, numbers, and currencies.'
          )
          .optional()
          .nullable(),
        machineTranslationCode: z
          .string()
          .describe(
            'Language code used when content is sent for machine translation.'
          )
          .optional()
          .nullable(),
        displayName: z
          .string()
          .describe('Display name of the locale.')
          .max(30)
          .optional()
          .nullable(),
        regionOptions: z
          .array(
            z.object({
              flag: z
                .string()
                .describe('Flag code of the region.')
                .max(3)
                .optional()
                .nullable(),
              regionalFormat: z
                .string()
                .describe(
                  'Format for how to display data types such as dates, times, numbers, and currencies.'
                )
                .optional()
                .nullable(),
            })
          )
          .max(100)
          .optional(),
      })
    )
    .optional(),
});
export const CreateLocaleRequest = z.object({
  locale: z
    .object({
      _id: z
        .string()
        .describe(
          'Locale ID.\n\nThe locale ID is made up of the language and region codes supported by Wix. For example, `"en-US"` for U.S. English.'
        )
        .max(20)
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the locale is updated. To prevent conflicting changes, the existing revision must be passed when updating the locale object.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the locale was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the locale was last updated.')
        .optional()
        .nullable(),
      languageCode: z
        .string()
        .describe('Language code. For example, `"en"` for English.'),
      regionCode: z
        .string()
        .describe('Region code. For example, `"UK"` for United Kingdom.')
        .optional()
        .nullable(),
      visibility: z.enum(['HIDDEN', 'VISIBLE']).optional(),
      primaryLocale: z
        .boolean()
        .describe("Whether the locale is the site's primary locale.")
        .optional()
        .nullable(),
      visitorPrimaryLocale: z
        .boolean()
        .describe("Whether the locale is the site's visitor primary locale.")
        .optional()
        .nullable(),
      flag: z
        .string()
        .describe(
          'Flag icon as a 3-letter language code based on Wix\'s supported locales. For example, `"USA"` for the United States flag icon.'
        )
        .max(3)
        .optional()
        .nullable(),
      regionalFormat: z
        .string()
        .describe(
          'Regional format for determining how to display data types such as dates, times, numbers, and currencies. For example, `"en-US"` to format dates like this: `MM-DD-YYYY`.'
        )
        .optional()
        .nullable(),
      effectiveUrlStructure: z
        .enum(['SUBDIRECTORY', 'SUBDOMAIN', 'QUERY_PARAM'])
        .optional(),
      urlStructureOverride: z
        .object({
          urlStructure: z
            .enum(['SUBDIRECTORY', 'SUBDOMAIN', 'QUERY_PARAM'])
            .optional(),
        })
        .describe("Optional override for the locale's default URL structure.")
        .optional(),
      machineTranslationCode: z
        .string()
        .describe(
          'Language code based on Wix\'s supported locales, used to specify the target language when\ntranslatable content is sent for machine translation. For example, `"ES"` for Spanish\nor `"ZH_TW"` for Traditional Chinese.\n\nDefault: The locale\'s `languageCode` value.'
        )
        .optional()
        .nullable(),
      effectiveDisplayName: z
        .string()
        .describe("The locale's `displayName`, unless overridden.")
        .max(30)
        .optional()
        .nullable(),
      displayName: z
        .string()
        .describe(
          'Default display name for the locale, inferred from the locale ID and region code.'
        )
        .max(30)
        .optional()
        .nullable(),
      overrideDisplayName: z
        .string()
        .describe("Optional override for the locale's display name.")
        .max(30)
        .optional()
        .nullable(),
    })
    .describe('Locale to create.'),
});
export const CreateLocaleResponse = z.object({
  _id: z
    .string()
    .describe(
      'Locale ID.\n\nThe locale ID is made up of the language and region codes supported by Wix. For example, `"en-US"` for U.S. English.'
    )
    .max(20)
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the locale is updated. To prevent conflicting changes, the existing revision must be passed when updating the locale object.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the locale was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the locale was last updated.')
    .optional()
    .nullable(),
  languageCode: z
    .string()
    .describe('Language code. For example, `"en"` for English.')
    .optional()
    .nullable(),
  regionCode: z
    .string()
    .describe('Region code. For example, `"UK"` for United Kingdom.')
    .optional()
    .nullable(),
  visibility: z
    .enum(['HIDDEN', 'VISIBLE'])
    .describe('Locale visibility status.')
    .optional(),
  primaryLocale: z
    .boolean()
    .describe("Whether the locale is the site's primary locale.")
    .optional()
    .nullable(),
  visitorPrimaryLocale: z
    .boolean()
    .describe("Whether the locale is the site's visitor primary locale.")
    .optional()
    .nullable(),
  flag: z
    .string()
    .describe(
      'Flag icon as a 3-letter language code based on Wix\'s supported locales. For example, `"USA"` for the United States flag icon.'
    )
    .max(3)
    .optional()
    .nullable(),
  regionalFormat: z
    .string()
    .describe(
      'Regional format for determining how to display data types such as dates, times, numbers, and currencies. For example, `"en-US"` to format dates like this: `MM-DD-YYYY`.'
    )
    .optional()
    .nullable(),
  effectiveUrlStructure: z
    .enum(['SUBDIRECTORY', 'SUBDOMAIN', 'QUERY_PARAM'])
    .describe(
      "Default URL structure for the locale's live site URL,\nbased on the locale's settings, unless overridden."
    )
    .optional(),
  urlStructureOverride: z
    .object({
      urlStructure: z
        .enum(['SUBDIRECTORY', 'SUBDOMAIN', 'QUERY_PARAM'])
        .describe('URL structure to use instead of the default.')
        .optional(),
    })
    .describe("Optional override for the locale's default URL structure.")
    .optional(),
  machineTranslationCode: z
    .string()
    .describe(
      'Language code based on Wix\'s supported locales, used to specify the target language when\ntranslatable content is sent for machine translation. For example, `"ES"` for Spanish\nor `"ZH_TW"` for Traditional Chinese.\n\nDefault: The locale\'s `languageCode` value.'
    )
    .optional()
    .nullable(),
  effectiveDisplayName: z
    .string()
    .describe("The locale's `displayName`, unless overridden.")
    .max(30)
    .optional()
    .nullable(),
  displayName: z
    .string()
    .describe(
      'Default display name for the locale, inferred from the locale ID and region code.'
    )
    .max(30)
    .optional()
    .nullable(),
  overrideDisplayName: z
    .string()
    .describe("Optional override for the locale's display name.")
    .max(30)
    .optional()
    .nullable(),
});
export const GetLocaleRequest = z.object({
  localeId: z.string().describe('ID of the locale to retrieve.').max(20),
});
export const GetLocaleResponse = z.object({
  _id: z
    .string()
    .describe(
      'Locale ID.\n\nThe locale ID is made up of the language and region codes supported by Wix. For example, `"en-US"` for U.S. English.'
    )
    .max(20)
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the locale is updated. To prevent conflicting changes, the existing revision must be passed when updating the locale object.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the locale was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the locale was last updated.')
    .optional()
    .nullable(),
  languageCode: z
    .string()
    .describe('Language code. For example, `"en"` for English.')
    .optional()
    .nullable(),
  regionCode: z
    .string()
    .describe('Region code. For example, `"UK"` for United Kingdom.')
    .optional()
    .nullable(),
  visibility: z
    .enum(['HIDDEN', 'VISIBLE'])
    .describe('Locale visibility status.')
    .optional(),
  primaryLocale: z
    .boolean()
    .describe("Whether the locale is the site's primary locale.")
    .optional()
    .nullable(),
  visitorPrimaryLocale: z
    .boolean()
    .describe("Whether the locale is the site's visitor primary locale.")
    .optional()
    .nullable(),
  flag: z
    .string()
    .describe(
      'Flag icon as a 3-letter language code based on Wix\'s supported locales. For example, `"USA"` for the United States flag icon.'
    )
    .max(3)
    .optional()
    .nullable(),
  regionalFormat: z
    .string()
    .describe(
      'Regional format for determining how to display data types such as dates, times, numbers, and currencies. For example, `"en-US"` to format dates like this: `MM-DD-YYYY`.'
    )
    .optional()
    .nullable(),
  effectiveUrlStructure: z
    .enum(['SUBDIRECTORY', 'SUBDOMAIN', 'QUERY_PARAM'])
    .describe(
      "Default URL structure for the locale's live site URL,\nbased on the locale's settings, unless overridden."
    )
    .optional(),
  urlStructureOverride: z
    .object({
      urlStructure: z
        .enum(['SUBDIRECTORY', 'SUBDOMAIN', 'QUERY_PARAM'])
        .describe('URL structure to use instead of the default.')
        .optional(),
    })
    .describe("Optional override for the locale's default URL structure.")
    .optional(),
  machineTranslationCode: z
    .string()
    .describe(
      'Language code based on Wix\'s supported locales, used to specify the target language when\ntranslatable content is sent for machine translation. For example, `"ES"` for Spanish\nor `"ZH_TW"` for Traditional Chinese.\n\nDefault: The locale\'s `languageCode` value.'
    )
    .optional()
    .nullable(),
  effectiveDisplayName: z
    .string()
    .describe("The locale's `displayName`, unless overridden.")
    .max(30)
    .optional()
    .nullable(),
  displayName: z
    .string()
    .describe(
      'Default display name for the locale, inferred from the locale ID and region code.'
    )
    .max(30)
    .optional()
    .nullable(),
  overrideDisplayName: z
    .string()
    .describe("Optional override for the locale's display name.")
    .max(30)
    .optional()
    .nullable(),
});
export const UpdateLocaleRequest = z.object({
  _id: z
    .string()
    .describe(
      'Locale ID.\n\nThe locale ID is made up of the language and region codes supported by Wix. For example, `"en-US"` for U.S. English.'
    )
    .max(20),
  locale: z
    .object({
      _id: z
        .string()
        .describe(
          'Locale ID.\n\nThe locale ID is made up of the language and region codes supported by Wix. For example, `"en-US"` for U.S. English.'
        )
        .max(20)
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the locale is updated. To prevent conflicting changes, the existing revision must be passed when updating the locale object.'
        ),
      _createdDate: z
        .date()
        .describe('Date and time the locale was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the locale was last updated.')
        .optional()
        .nullable(),
      languageCode: z
        .string()
        .describe('Language code. For example, `"en"` for English.')
        .optional()
        .nullable(),
      regionCode: z
        .string()
        .describe('Region code. For example, `"UK"` for United Kingdom.')
        .optional()
        .nullable(),
      visibility: z.enum(['HIDDEN', 'VISIBLE']).optional(),
      primaryLocale: z
        .boolean()
        .describe("Whether the locale is the site's primary locale.")
        .optional()
        .nullable(),
      visitorPrimaryLocale: z
        .boolean()
        .describe("Whether the locale is the site's visitor primary locale.")
        .optional()
        .nullable(),
      flag: z
        .string()
        .describe(
          'Flag icon as a 3-letter language code based on Wix\'s supported locales. For example, `"USA"` for the United States flag icon.'
        )
        .max(3)
        .optional()
        .nullable(),
      regionalFormat: z
        .string()
        .describe(
          'Regional format for determining how to display data types such as dates, times, numbers, and currencies. For example, `"en-US"` to format dates like this: `MM-DD-YYYY`.'
        )
        .optional()
        .nullable(),
      effectiveUrlStructure: z
        .enum(['SUBDIRECTORY', 'SUBDOMAIN', 'QUERY_PARAM'])
        .optional(),
      urlStructureOverride: z
        .object({
          urlStructure: z
            .enum(['SUBDIRECTORY', 'SUBDOMAIN', 'QUERY_PARAM'])
            .optional(),
        })
        .describe("Optional override for the locale's default URL structure.")
        .optional(),
      machineTranslationCode: z
        .string()
        .describe(
          'Language code based on Wix\'s supported locales, used to specify the target language when\ntranslatable content is sent for machine translation. For example, `"ES"` for Spanish\nor `"ZH_TW"` for Traditional Chinese.\n\nDefault: The locale\'s `languageCode` value.'
        )
        .optional()
        .nullable(),
      effectiveDisplayName: z
        .string()
        .describe("The locale's `displayName`, unless overridden.")
        .max(30)
        .optional()
        .nullable(),
      displayName: z
        .string()
        .describe(
          'Default display name for the locale, inferred from the locale ID and region code.'
        )
        .max(30)
        .optional()
        .nullable(),
      overrideDisplayName: z
        .string()
        .describe("Optional override for the locale's display name.")
        .max(30)
        .optional()
        .nullable(),
    })
    .describe('Locale to update, may be partial.'),
});
export const UpdateLocaleResponse = z.object({
  _id: z
    .string()
    .describe(
      'Locale ID.\n\nThe locale ID is made up of the language and region codes supported by Wix. For example, `"en-US"` for U.S. English.'
    )
    .max(20)
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the locale is updated. To prevent conflicting changes, the existing revision must be passed when updating the locale object.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the locale was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the locale was last updated.')
    .optional()
    .nullable(),
  languageCode: z
    .string()
    .describe('Language code. For example, `"en"` for English.')
    .optional()
    .nullable(),
  regionCode: z
    .string()
    .describe('Region code. For example, `"UK"` for United Kingdom.')
    .optional()
    .nullable(),
  visibility: z
    .enum(['HIDDEN', 'VISIBLE'])
    .describe('Locale visibility status.')
    .optional(),
  primaryLocale: z
    .boolean()
    .describe("Whether the locale is the site's primary locale.")
    .optional()
    .nullable(),
  visitorPrimaryLocale: z
    .boolean()
    .describe("Whether the locale is the site's visitor primary locale.")
    .optional()
    .nullable(),
  flag: z
    .string()
    .describe(
      'Flag icon as a 3-letter language code based on Wix\'s supported locales. For example, `"USA"` for the United States flag icon.'
    )
    .max(3)
    .optional()
    .nullable(),
  regionalFormat: z
    .string()
    .describe(
      'Regional format for determining how to display data types such as dates, times, numbers, and currencies. For example, `"en-US"` to format dates like this: `MM-DD-YYYY`.'
    )
    .optional()
    .nullable(),
  effectiveUrlStructure: z
    .enum(['SUBDIRECTORY', 'SUBDOMAIN', 'QUERY_PARAM'])
    .describe(
      "Default URL structure for the locale's live site URL,\nbased on the locale's settings, unless overridden."
    )
    .optional(),
  urlStructureOverride: z
    .object({
      urlStructure: z
        .enum(['SUBDIRECTORY', 'SUBDOMAIN', 'QUERY_PARAM'])
        .describe('URL structure to use instead of the default.')
        .optional(),
    })
    .describe("Optional override for the locale's default URL structure.")
    .optional(),
  machineTranslationCode: z
    .string()
    .describe(
      'Language code based on Wix\'s supported locales, used to specify the target language when\ntranslatable content is sent for machine translation. For example, `"ES"` for Spanish\nor `"ZH_TW"` for Traditional Chinese.\n\nDefault: The locale\'s `languageCode` value.'
    )
    .optional()
    .nullable(),
  effectiveDisplayName: z
    .string()
    .describe("The locale's `displayName`, unless overridden.")
    .max(30)
    .optional()
    .nullable(),
  displayName: z
    .string()
    .describe(
      'Default display name for the locale, inferred from the locale ID and region code.'
    )
    .max(30)
    .optional()
    .nullable(),
  overrideDisplayName: z
    .string()
    .describe("Optional override for the locale's display name.")
    .max(30)
    .optional()
    .nullable(),
});
export const DeleteLocaleRequest = z.object({
  localeId: z.string().describe('ID of the locale to delete.').max(20),
});
export const DeleteLocaleResponse = z.object({});
export const QueryLocalesRequest = z.object({
  query: z
    .intersection(
      z.object({
        filter: z
          .record(z.string(), z.any())
          .describe(
            'Filter object in the following format:\n`"filter" : {\n"fieldName1": "value1",\n"fieldName2":{"$operator":"value2"}\n}`\nExample of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`'
          )
          .optional()
          .nullable(),
        sort: z
          .array(
            z.object({
              fieldName: z
                .string()
                .describe('Name of the field to sort by.')
                .max(512)
                .optional(),
              order: z.enum(['ASC', 'DESC']).optional(),
            })
          )
          .max(5)
          .optional(),
      }),
      z.xor([
        z.object({ cursorPaging: z.never().optional() }),
        z.object({
          cursorPaging: z
            .object({
              limit: z
                .number()
                .int()
                .describe('Maximum number of items to return in the results.')
                .min(0)
                .max(100)
                .optional()
                .nullable(),
              cursor: z
                .string()
                .describe(
                  "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
                )
                .max(16000)
                .optional()
                .nullable(),
            })
            .describe(
              'Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.'
            ),
        }),
      ])
    )
    .describe('Query options.'),
});
export const QueryLocalesResponse = z.object({
  locales: z
    .array(
      z.object({
        _id: z
          .string()
          .describe(
            'Locale ID.\n\nThe locale ID is made up of the language and region codes supported by Wix. For example, `"en-US"` for U.S. English.'
          )
          .max(20)
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the locale is updated. To prevent conflicting changes, the existing revision must be passed when updating the locale object.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the locale was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the locale was last updated.')
          .optional()
          .nullable(),
        languageCode: z
          .string()
          .describe('Language code. For example, `"en"` for English.')
          .optional()
          .nullable(),
        regionCode: z
          .string()
          .describe('Region code. For example, `"UK"` for United Kingdom.')
          .optional()
          .nullable(),
        visibility: z
          .enum(['HIDDEN', 'VISIBLE'])
          .describe('Locale visibility status.')
          .optional(),
        primaryLocale: z
          .boolean()
          .describe("Whether the locale is the site's primary locale.")
          .optional()
          .nullable(),
        visitorPrimaryLocale: z
          .boolean()
          .describe("Whether the locale is the site's visitor primary locale.")
          .optional()
          .nullable(),
        flag: z
          .string()
          .describe(
            'Flag icon as a 3-letter language code based on Wix\'s supported locales. For example, `"USA"` for the United States flag icon.'
          )
          .max(3)
          .optional()
          .nullable(),
        regionalFormat: z
          .string()
          .describe(
            'Regional format for determining how to display data types such as dates, times, numbers, and currencies. For example, `"en-US"` to format dates like this: `MM-DD-YYYY`.'
          )
          .optional()
          .nullable(),
        effectiveUrlStructure: z
          .enum(['SUBDIRECTORY', 'SUBDOMAIN', 'QUERY_PARAM'])
          .describe(
            "Default URL structure for the locale's live site URL,\nbased on the locale's settings, unless overridden."
          )
          .optional(),
        urlStructureOverride: z
          .object({
            urlStructure: z
              .enum(['SUBDIRECTORY', 'SUBDOMAIN', 'QUERY_PARAM'])
              .describe('URL structure to use instead of the default.')
              .optional(),
          })
          .describe("Optional override for the locale's default URL structure.")
          .optional(),
        machineTranslationCode: z
          .string()
          .describe(
            'Language code based on Wix\'s supported locales, used to specify the target language when\ntranslatable content is sent for machine translation. For example, `"ES"` for Spanish\nor `"ZH_TW"` for Traditional Chinese.\n\nDefault: The locale\'s `languageCode` value.'
          )
          .optional()
          .nullable(),
        effectiveDisplayName: z
          .string()
          .describe("The locale's `displayName`, unless overridden.")
          .max(30)
          .optional()
          .nullable(),
        displayName: z
          .string()
          .describe(
            'Default display name for the locale, inferred from the locale ID and region code.'
          )
          .max(30)
          .optional()
          .nullable(),
        overrideDisplayName: z
          .string()
          .describe("Optional override for the locale's display name.")
          .max(30)
          .optional()
          .nullable(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Paging metadata.')
    .optional(),
});
export const BulkCreateLocalesRequest = z.object({
  locales: z
    .array(
      z.object({
        _id: z
          .string()
          .describe(
            'Locale ID.\n\nThe locale ID is made up of the language and region codes supported by Wix. For example, `"en-US"` for U.S. English.'
          )
          .max(20)
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the locale is updated. To prevent conflicting changes, the existing revision must be passed when updating the locale object.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the locale was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the locale was last updated.')
          .optional()
          .nullable(),
        languageCode: z
          .string()
          .describe('Language code. For example, `"en"` for English.'),
        regionCode: z
          .string()
          .describe('Region code. For example, `"UK"` for United Kingdom.')
          .optional()
          .nullable(),
        visibility: z.enum(['HIDDEN', 'VISIBLE']).optional(),
        primaryLocale: z
          .boolean()
          .describe("Whether the locale is the site's primary locale.")
          .optional()
          .nullable(),
        visitorPrimaryLocale: z
          .boolean()
          .describe("Whether the locale is the site's visitor primary locale.")
          .optional()
          .nullable(),
        flag: z
          .string()
          .describe(
            'Flag icon as a 3-letter language code based on Wix\'s supported locales. For example, `"USA"` for the United States flag icon.'
          )
          .max(3)
          .optional()
          .nullable(),
        regionalFormat: z
          .string()
          .describe(
            'Regional format for determining how to display data types such as dates, times, numbers, and currencies. For example, `"en-US"` to format dates like this: `MM-DD-YYYY`.'
          )
          .optional()
          .nullable(),
        effectiveUrlStructure: z
          .enum(['SUBDIRECTORY', 'SUBDOMAIN', 'QUERY_PARAM'])
          .optional(),
        urlStructureOverride: z
          .object({
            urlStructure: z
              .enum(['SUBDIRECTORY', 'SUBDOMAIN', 'QUERY_PARAM'])
              .optional(),
          })
          .describe("Optional override for the locale's default URL structure.")
          .optional(),
        machineTranslationCode: z
          .string()
          .describe(
            'Language code based on Wix\'s supported locales, used to specify the target language when\ntranslatable content is sent for machine translation. For example, `"ES"` for Spanish\nor `"ZH_TW"` for Traditional Chinese.\n\nDefault: The locale\'s `languageCode` value.'
          )
          .optional()
          .nullable(),
        effectiveDisplayName: z
          .string()
          .describe("The locale's `displayName`, unless overridden.")
          .max(30)
          .optional()
          .nullable(),
        displayName: z
          .string()
          .describe(
            'Default display name for the locale, inferred from the locale ID and region code.'
          )
          .max(30)
          .optional()
          .nullable(),
        overrideDisplayName: z
          .string()
          .describe("Optional override for the locale's display name.")
          .max(30)
          .optional()
          .nullable(),
      })
    )
    .min(1)
    .max(100),
  options: z
    .object({
      returnEntity: z
        .boolean()
        .describe('Whether to receive the created locales in the response.')
        .optional(),
    })
    .optional(),
});
export const BulkCreateLocalesResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Item metadata.')
          .optional(),
        item: z
          .object({
            _id: z
              .string()
              .describe(
                'Locale ID.\n\nThe locale ID is made up of the language and region codes supported by Wix. For example, `"en-US"` for U.S. English.'
              )
              .max(20)
              .optional()
              .nullable(),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number, which increments by 1 each time the locale is updated. To prevent conflicting changes, the existing revision must be passed when updating the locale object.'
              )
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe('Date and time the locale was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe('Date and time the locale was last updated.')
              .optional()
              .nullable(),
            languageCode: z
              .string()
              .describe('Language code. For example, `"en"` for English.')
              .optional()
              .nullable(),
            regionCode: z
              .string()
              .describe('Region code. For example, `"UK"` for United Kingdom.')
              .optional()
              .nullable(),
            visibility: z
              .enum(['HIDDEN', 'VISIBLE'])
              .describe('Locale visibility status.')
              .optional(),
            primaryLocale: z
              .boolean()
              .describe("Whether the locale is the site's primary locale.")
              .optional()
              .nullable(),
            visitorPrimaryLocale: z
              .boolean()
              .describe(
                "Whether the locale is the site's visitor primary locale."
              )
              .optional()
              .nullable(),
            flag: z
              .string()
              .describe(
                'Flag icon as a 3-letter language code based on Wix\'s supported locales. For example, `"USA"` for the United States flag icon.'
              )
              .max(3)
              .optional()
              .nullable(),
            regionalFormat: z
              .string()
              .describe(
                'Regional format for determining how to display data types such as dates, times, numbers, and currencies. For example, `"en-US"` to format dates like this: `MM-DD-YYYY`.'
              )
              .optional()
              .nullable(),
            effectiveUrlStructure: z
              .enum(['SUBDIRECTORY', 'SUBDOMAIN', 'QUERY_PARAM'])
              .describe(
                "Default URL structure for the locale's live site URL,\nbased on the locale's settings, unless overridden."
              )
              .optional(),
            urlStructureOverride: z
              .object({
                urlStructure: z
                  .enum(['SUBDIRECTORY', 'SUBDOMAIN', 'QUERY_PARAM'])
                  .describe('URL structure to use instead of the default.')
                  .optional(),
              })
              .describe(
                "Optional override for the locale's default URL structure."
              )
              .optional(),
            machineTranslationCode: z
              .string()
              .describe(
                'Language code based on Wix\'s supported locales, used to specify the target language when\ntranslatable content is sent for machine translation. For example, `"ES"` for Spanish\nor `"ZH_TW"` for Traditional Chinese.\n\nDefault: The locale\'s `languageCode` value.'
              )
              .optional()
              .nullable(),
            effectiveDisplayName: z
              .string()
              .describe("The locale's `displayName`, unless overridden.")
              .max(30)
              .optional()
              .nullable(),
            displayName: z
              .string()
              .describe(
                'Default display name for the locale, inferred from the locale ID and region code.'
              )
              .max(30)
              .optional()
              .nullable(),
            overrideDisplayName: z
              .string()
              .describe("Optional override for the locale's display name.")
              .max(30)
              .optional()
              .nullable(),
          })
          .describe(
            'Created locale. Returned when `returnEntity` is set to `true`.'
          )
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Bulk metadata.')
    .optional(),
});
export const BulkUpdateLocalesRequest = z.object({
  locales: z
    .array(
      z.object({
        locale: z
          .object({
            _id: z
              .string()
              .describe(
                'Locale ID.\n\nThe locale ID is made up of the language and region codes supported by Wix. For example, `"en-US"` for U.S. English.'
              )
              .max(20),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number, which increments by 1 each time the locale is updated. To prevent conflicting changes, the existing revision must be passed when updating the locale object.'
              ),
            _createdDate: z
              .date()
              .describe('Date and time the locale was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe('Date and time the locale was last updated.')
              .optional()
              .nullable(),
            languageCode: z
              .string()
              .describe('Language code. For example, `"en"` for English.')
              .optional()
              .nullable(),
            regionCode: z
              .string()
              .describe('Region code. For example, `"UK"` for United Kingdom.')
              .optional()
              .nullable(),
            visibility: z.enum(['HIDDEN', 'VISIBLE']).optional(),
            primaryLocale: z
              .boolean()
              .describe("Whether the locale is the site's primary locale.")
              .optional()
              .nullable(),
            visitorPrimaryLocale: z
              .boolean()
              .describe(
                "Whether the locale is the site's visitor primary locale."
              )
              .optional()
              .nullable(),
            flag: z
              .string()
              .describe(
                'Flag icon as a 3-letter language code based on Wix\'s supported locales. For example, `"USA"` for the United States flag icon.'
              )
              .max(3)
              .optional()
              .nullable(),
            regionalFormat: z
              .string()
              .describe(
                'Regional format for determining how to display data types such as dates, times, numbers, and currencies. For example, `"en-US"` to format dates like this: `MM-DD-YYYY`.'
              )
              .optional()
              .nullable(),
            effectiveUrlStructure: z
              .enum(['SUBDIRECTORY', 'SUBDOMAIN', 'QUERY_PARAM'])
              .optional(),
            urlStructureOverride: z
              .object({
                urlStructure: z
                  .enum(['SUBDIRECTORY', 'SUBDOMAIN', 'QUERY_PARAM'])
                  .optional(),
              })
              .describe(
                "Optional override for the locale's default URL structure."
              )
              .optional(),
            machineTranslationCode: z
              .string()
              .describe(
                'Language code based on Wix\'s supported locales, used to specify the target language when\ntranslatable content is sent for machine translation. For example, `"ES"` for Spanish\nor `"ZH_TW"` for Traditional Chinese.\n\nDefault: The locale\'s `languageCode` value.'
              )
              .optional()
              .nullable(),
            effectiveDisplayName: z
              .string()
              .describe("The locale's `displayName`, unless overridden.")
              .max(30)
              .optional()
              .nullable(),
            displayName: z
              .string()
              .describe(
                'Default display name for the locale, inferred from the locale ID and region code.'
              )
              .max(30)
              .optional()
              .nullable(),
            overrideDisplayName: z
              .string()
              .describe("Optional override for the locale's display name.")
              .max(30)
              .optional()
              .nullable(),
          })
          .describe('Locales to update, may be partial.')
          .optional(),
      })
    )
    .min(1)
    .max(100),
  options: z
    .object({
      returnEntity: z
        .boolean()
        .describe('Whether to receive the updated locales in the response.')
        .optional(),
    })
    .optional(),
});
export const BulkUpdateLocalesResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Item metadata.')
          .optional(),
        item: z
          .object({
            _id: z
              .string()
              .describe(
                'Locale ID.\n\nThe locale ID is made up of the language and region codes supported by Wix. For example, `"en-US"` for U.S. English.'
              )
              .max(20)
              .optional()
              .nullable(),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number, which increments by 1 each time the locale is updated. To prevent conflicting changes, the existing revision must be passed when updating the locale object.'
              )
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe('Date and time the locale was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe('Date and time the locale was last updated.')
              .optional()
              .nullable(),
            languageCode: z
              .string()
              .describe('Language code. For example, `"en"` for English.')
              .optional()
              .nullable(),
            regionCode: z
              .string()
              .describe('Region code. For example, `"UK"` for United Kingdom.')
              .optional()
              .nullable(),
            visibility: z
              .enum(['HIDDEN', 'VISIBLE'])
              .describe('Locale visibility status.')
              .optional(),
            primaryLocale: z
              .boolean()
              .describe("Whether the locale is the site's primary locale.")
              .optional()
              .nullable(),
            visitorPrimaryLocale: z
              .boolean()
              .describe(
                "Whether the locale is the site's visitor primary locale."
              )
              .optional()
              .nullable(),
            flag: z
              .string()
              .describe(
                'Flag icon as a 3-letter language code based on Wix\'s supported locales. For example, `"USA"` for the United States flag icon.'
              )
              .max(3)
              .optional()
              .nullable(),
            regionalFormat: z
              .string()
              .describe(
                'Regional format for determining how to display data types such as dates, times, numbers, and currencies. For example, `"en-US"` to format dates like this: `MM-DD-YYYY`.'
              )
              .optional()
              .nullable(),
            effectiveUrlStructure: z
              .enum(['SUBDIRECTORY', 'SUBDOMAIN', 'QUERY_PARAM'])
              .describe(
                "Default URL structure for the locale's live site URL,\nbased on the locale's settings, unless overridden."
              )
              .optional(),
            urlStructureOverride: z
              .object({
                urlStructure: z
                  .enum(['SUBDIRECTORY', 'SUBDOMAIN', 'QUERY_PARAM'])
                  .describe('URL structure to use instead of the default.')
                  .optional(),
              })
              .describe(
                "Optional override for the locale's default URL structure."
              )
              .optional(),
            machineTranslationCode: z
              .string()
              .describe(
                'Language code based on Wix\'s supported locales, used to specify the target language when\ntranslatable content is sent for machine translation. For example, `"ES"` for Spanish\nor `"ZH_TW"` for Traditional Chinese.\n\nDefault: The locale\'s `languageCode` value.'
              )
              .optional()
              .nullable(),
            effectiveDisplayName: z
              .string()
              .describe("The locale's `displayName`, unless overridden.")
              .max(30)
              .optional()
              .nullable(),
            displayName: z
              .string()
              .describe(
                'Default display name for the locale, inferred from the locale ID and region code.'
              )
              .max(30)
              .optional()
              .nullable(),
            overrideDisplayName: z
              .string()
              .describe("Optional override for the locale's display name.")
              .max(30)
              .optional()
              .nullable(),
          })
          .describe(
            'Updated locale. Returned when `returnEntity` is set to `true`.'
          )
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Bulk metadata.')
    .optional(),
});
export const BulkDeleteLocalesRequest = z.object({
  localeIds: z.array(z.string()).min(1).max(100),
});
export const BulkDeleteLocalesResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Item metadata.')
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Bulk metadata.')
    .optional(),
});
export const CreateNewPrimaryLocaleRequest = z.object({
  primaryLocale: z
    .object({
      _id: z
        .string()
        .describe(
          'Locale ID.\n\nThe locale ID is made up of the language and region codes supported by Wix. For example, `"en-US"` for U.S. English.'
        )
        .max(20)
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the locale is updated. To prevent conflicting changes, the existing revision must be passed when updating the locale object.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the locale was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the locale was last updated.')
        .optional()
        .nullable(),
      languageCode: z
        .string()
        .describe('Language code. For example, `"en"` for English.')
        .optional()
        .nullable(),
      regionCode: z
        .string()
        .describe('Region code. For example, `"UK"` for United Kingdom.')
        .optional()
        .nullable(),
      visibility: z.enum(['HIDDEN', 'VISIBLE']).optional(),
      primaryLocale: z
        .boolean()
        .describe("Whether the locale is the site's primary locale.")
        .optional()
        .nullable(),
      visitorPrimaryLocale: z
        .boolean()
        .describe("Whether the locale is the site's visitor primary locale.")
        .optional()
        .nullable(),
      flag: z
        .string()
        .describe(
          'Flag icon as a 3-letter language code based on Wix\'s supported locales. For example, `"USA"` for the United States flag icon.'
        )
        .max(3)
        .optional()
        .nullable(),
      regionalFormat: z
        .string()
        .describe(
          'Regional format for determining how to display data types such as dates, times, numbers, and currencies. For example, `"en-US"` to format dates like this: `MM-DD-YYYY`.'
        )
        .optional()
        .nullable(),
      effectiveUrlStructure: z
        .enum(['SUBDIRECTORY', 'SUBDOMAIN', 'QUERY_PARAM'])
        .optional(),
      urlStructureOverride: z
        .object({
          urlStructure: z
            .enum(['SUBDIRECTORY', 'SUBDOMAIN', 'QUERY_PARAM'])
            .optional(),
        })
        .describe("Optional override for the locale's default URL structure.")
        .optional(),
      machineTranslationCode: z
        .string()
        .describe(
          'Language code based on Wix\'s supported locales, used to specify the target language when\ntranslatable content is sent for machine translation. For example, `"ES"` for Spanish\nor `"ZH_TW"` for Traditional Chinese.\n\nDefault: The locale\'s `languageCode` value.'
        )
        .optional()
        .nullable(),
      effectiveDisplayName: z
        .string()
        .describe("The locale's `displayName`, unless overridden.")
        .max(30)
        .optional()
        .nullable(),
      displayName: z
        .string()
        .describe(
          'Default display name for the locale, inferred from the locale ID and region code.'
        )
        .max(30)
        .optional()
        .nullable(),
      overrideDisplayName: z
        .string()
        .describe("Optional override for the locale's display name.")
        .max(30)
        .optional()
        .nullable(),
    })
    .describe('New primary locale.'),
});
export const CreateNewPrimaryLocaleResponse = z.object({
  token: z
    .string()
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
});
export const GetNewPrimaryLocaleStatusRequest = z.object({
  token: z
    .string()
    .describe(
      'Token to retrieve the process status. Returned in the response of Create New Primary Locale.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetNewPrimaryLocaleStatusResponse = z.intersection(
  z.object({
    status: z
      .enum(['IN_PROGRESS', 'FINISH_SUCCESSFUL', 'FINISH_WITH_ERROR'])
      .describe('The current status of the Create New Primary Locale method.')
      .optional(),
  }),
  z.xor([
    z.object({
      finishSuccessfulData: z.never().optional(),
      finishWithErrorData: z.never().optional(),
    }),
    z.object({
      finishWithErrorData: z.never().optional(),
      finishSuccessfulData: z
        .object({
          newPrimaryLocale: z
            .object({
              _id: z
                .string()
                .describe(
                  'Locale ID.\n\nThe locale ID is made up of the language and region codes supported by Wix. For example, `"en-US"` for U.S. English.'
                )
                .max(20)
                .optional()
                .nullable(),
              revision: z
                .string()
                .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                .describe(
                  'Revision number, which increments by 1 each time the locale is updated. To prevent conflicting changes, the existing revision must be passed when updating the locale object.'
                )
                .optional()
                .nullable(),
              _createdDate: z
                .date()
                .describe('Date and time the locale was created.')
                .optional()
                .nullable(),
              _updatedDate: z
                .date()
                .describe('Date and time the locale was last updated.')
                .optional()
                .nullable(),
              languageCode: z
                .string()
                .describe('Language code. For example, `"en"` for English.')
                .optional()
                .nullable(),
              regionCode: z
                .string()
                .describe(
                  'Region code. For example, `"UK"` for United Kingdom.'
                )
                .optional()
                .nullable(),
              visibility: z
                .enum(['HIDDEN', 'VISIBLE'])
                .describe('Locale visibility status.')
                .optional(),
              primaryLocale: z
                .boolean()
                .describe("Whether the locale is the site's primary locale.")
                .optional()
                .nullable(),
              visitorPrimaryLocale: z
                .boolean()
                .describe(
                  "Whether the locale is the site's visitor primary locale."
                )
                .optional()
                .nullable(),
              flag: z
                .string()
                .describe(
                  'Flag icon as a 3-letter language code based on Wix\'s supported locales. For example, `"USA"` for the United States flag icon.'
                )
                .max(3)
                .optional()
                .nullable(),
              regionalFormat: z
                .string()
                .describe(
                  'Regional format for determining how to display data types such as dates, times, numbers, and currencies. For example, `"en-US"` to format dates like this: `MM-DD-YYYY`.'
                )
                .optional()
                .nullable(),
              effectiveUrlStructure: z
                .enum(['SUBDIRECTORY', 'SUBDOMAIN', 'QUERY_PARAM'])
                .describe(
                  "Default URL structure for the locale's live site URL,\nbased on the locale's settings, unless overridden."
                )
                .optional(),
              urlStructureOverride: z
                .object({
                  urlStructure: z
                    .enum(['SUBDIRECTORY', 'SUBDOMAIN', 'QUERY_PARAM'])
                    .describe('URL structure to use instead of the default.')
                    .optional(),
                })
                .describe(
                  "Optional override for the locale's default URL structure."
                )
                .optional(),
              machineTranslationCode: z
                .string()
                .describe(
                  'Language code based on Wix\'s supported locales, used to specify the target language when\ntranslatable content is sent for machine translation. For example, `"ES"` for Spanish\nor `"ZH_TW"` for Traditional Chinese.\n\nDefault: The locale\'s `languageCode` value.'
                )
                .optional()
                .nullable(),
              effectiveDisplayName: z
                .string()
                .describe("The locale's `displayName`, unless overridden.")
                .max(30)
                .optional()
                .nullable(),
              displayName: z
                .string()
                .describe(
                  'Default display name for the locale, inferred from the locale ID and region code.'
                )
                .max(30)
                .optional()
                .nullable(),
              overrideDisplayName: z
                .string()
                .describe("Optional override for the locale's display name.")
                .max(30)
                .optional()
                .nullable(),
            })
            .describe('The new primary locale.')
            .optional(),
        })
        .describe(
          'New primary locale data when the process successfully completes.'
        ),
    }),
    z.object({
      finishSuccessfulData: z.never().optional(),
      finishWithErrorData: z
        .object({
          error: z
            .object({
              code: z.string().describe('Error code.').optional(),
              description: z
                .string()
                .describe('Description of the error.')
                .optional(),
              data: z
                .record(z.string(), z.any())
                .describe('Data related to the error.')
                .optional()
                .nullable(),
            })
            .describe('Error details.')
            .optional(),
        })
        .describe('Error details when the process fails.'),
    }),
  ])
);
export const SetVisitorPrimaryLocaleRequest = z.object({
  localeId: z.string().describe('ID of the locale to set as visitor primary.'),
});
export const SetVisitorPrimaryLocaleResponse = z.object({
  locale: z
    .object({
      _id: z
        .string()
        .describe(
          'Locale ID.\n\nThe locale ID is made up of the language and region codes supported by Wix. For example, `"en-US"` for U.S. English.'
        )
        .max(20)
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the locale is updated. To prevent conflicting changes, the existing revision must be passed when updating the locale object.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the locale was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the locale was last updated.')
        .optional()
        .nullable(),
      languageCode: z
        .string()
        .describe('Language code. For example, `"en"` for English.')
        .optional()
        .nullable(),
      regionCode: z
        .string()
        .describe('Region code. For example, `"UK"` for United Kingdom.')
        .optional()
        .nullable(),
      visibility: z
        .enum(['HIDDEN', 'VISIBLE'])
        .describe('Locale visibility status.')
        .optional(),
      primaryLocale: z
        .boolean()
        .describe("Whether the locale is the site's primary locale.")
        .optional()
        .nullable(),
      visitorPrimaryLocale: z
        .boolean()
        .describe("Whether the locale is the site's visitor primary locale.")
        .optional()
        .nullable(),
      flag: z
        .string()
        .describe(
          'Flag icon as a 3-letter language code based on Wix\'s supported locales. For example, `"USA"` for the United States flag icon.'
        )
        .max(3)
        .optional()
        .nullable(),
      regionalFormat: z
        .string()
        .describe(
          'Regional format for determining how to display data types such as dates, times, numbers, and currencies. For example, `"en-US"` to format dates like this: `MM-DD-YYYY`.'
        )
        .optional()
        .nullable(),
      effectiveUrlStructure: z
        .enum(['SUBDIRECTORY', 'SUBDOMAIN', 'QUERY_PARAM'])
        .describe(
          "Default URL structure for the locale's live site URL,\nbased on the locale's settings, unless overridden."
        )
        .optional(),
      urlStructureOverride: z
        .object({
          urlStructure: z
            .enum(['SUBDIRECTORY', 'SUBDOMAIN', 'QUERY_PARAM'])
            .describe('URL structure to use instead of the default.')
            .optional(),
        })
        .describe("Optional override for the locale's default URL structure.")
        .optional(),
      machineTranslationCode: z
        .string()
        .describe(
          'Language code based on Wix\'s supported locales, used to specify the target language when\ntranslatable content is sent for machine translation. For example, `"ES"` for Spanish\nor `"ZH_TW"` for Traditional Chinese.\n\nDefault: The locale\'s `languageCode` value.'
        )
        .optional()
        .nullable(),
      effectiveDisplayName: z
        .string()
        .describe("The locale's `displayName`, unless overridden.")
        .max(30)
        .optional()
        .nullable(),
      displayName: z
        .string()
        .describe(
          'Default display name for the locale, inferred from the locale ID and region code.'
        )
        .max(30)
        .optional()
        .nullable(),
      overrideDisplayName: z
        .string()
        .describe("Optional override for the locale's display name.")
        .max(30)
        .optional()
        .nullable(),
    })
    .describe('Updated locale that is now set as the visitor primary locale.')
    .optional(),
});
